import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Eye, EyeOff, ImagePlus, MapPin, Pencil, Plus, Trash2, X } from 'lucide-react';
import AdminSection from '../components/AdminSection';
import ImagePicker from '../components/ImagePicker';
import Pagination from '../components/Pagination';
import { usePaged } from '../hooks/use-paged';
import { LANGS } from '../lib/adminActivities';
import { deleteDestination, fetchDestinations, saveDestination } from '../lib/adminApi';

const inputCls =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-500 outline-none transition focus:border-[#1fa5a3] focus:ring-4 focus:ring-[#1fa5a3]/10';

const EMPTY_FORM = { slug: '', name: {}, image: '', active: true };

const slugify = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const destName = (d) => (d.name && typeof d.name === 'object' ? d.name.es : d.name) || d.slug;

export default function AdminDestinations() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingSlug, setEditingSlug] = useState(null);
  const [lang, setLang] = useState('es');
  const [msg, setMsg] = useState({ ok: '', err: '' });
  const [picker, setPicker] = useState(false);

  const listQuery = useQuery({ queryKey: ['admin', 'destinations'], queryFn: fetchDestinations });
  const items = listQuery.data?.items || [];
  const { pageItems: pagedDestinations, page, setPage, pageCount, pageSize, setPageSize } = usePaged(items, 20);

  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));
  const setI18n = (value) => setForm((c) => ({ ...c, name: { ...c.name, [lang]: value } }));
  const resetForm = () => { setForm(EMPTY_FORM); setEditingSlug(null); setLang('es'); };

  const startEdit = (d) => {
    setForm({
      slug: d.slug,
      name: d.name && typeof d.name === 'object' ? { ...d.name } : d.name ? { es: d.name } : {},
      image: d.image || '',
      active: d.active !== false,
    });
    setEditingSlug(d.slug);
    setLang('es');
    setMsg({ ok: '', err: '' });
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'destinations'] });

  const saveMutation = useMutation({
    mutationFn: () => saveDestination(form.slug.trim(), {
      slug: form.slug.trim(),
      name: form.name,
      image: form.image.trim(),
      active: form.active,
    }),
    onSuccess: async () => {
      setMsg({ ok: editingSlug ? 'Destino actualizado.' : 'Destino creado.', err: '' });
      resetForm();
      await invalidate();
    },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo guardar el destino.' }),
  });

  const toggleMutation = useMutation({
    mutationFn: (d) => saveDestination(d.slug, { slug: d.slug, name: d.name, active: d.active === false }),
    onSuccess: async () => { setMsg({ ok: 'Visibilidad actualizada.', err: '' }); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo cambiar la visibilidad.' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (slug) => deleteDestination(slug),
    onSuccess: async () => { setMsg({ ok: 'Destino eliminado.', err: '' }); await invalidate(); },
    onError: (e) => setMsg({ ok: '', err: e.message || 'No se pudo eliminar.' }),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.slug.trim() || !(form.name?.es || '').trim()) {
      setMsg({ ok: '', err: 'La URL y el nombre en español son obligatorios.' });
      setLang('es');
      return;
    }
    saveMutation.mutate();
  };

  return (
    <AdminSection
      eyebrow="Admin"
      title="Destinos"
      description="Las islas y zonas donde se agrupan las actividades. El nombre se traduce a los seis idiomas."
      widthClassName="max-w-6xl"
    >
      {msg.ok ? <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{msg.ok}</div> : null}
      {msg.err ? <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{msg.err}</div> : null}

      <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
        <section className="min-w-0 rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <h2 className="text-2xl font-bold text-[#14213d]">{items.length} destinos</h2>
          <p className="mt-1 text-sm text-slate-500">Ocultar un destino no borra sus actividades.</p>

          <div className="mt-5 divide-y divide-slate-100">
            {pagedDestinations.map((d) => {
              const live = d.active !== false;
              return (
                <div key={d.slug} className={`flex items-center gap-3 py-3 ${live ? '' : 'rounded-xl bg-amber-50/60 px-2'}`}>
                  <img src={d.image} alt="" className={`h-12 w-16 flex-shrink-0 rounded-lg bg-slate-100 object-cover ${live ? '' : 'grayscale'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-[#14213d]">{destName(d)}</p>
                      {!live ? (
                        <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">
                          <EyeOff className="h-3 w-3" />Oculto
                        </span>
                      ) : null}
                    </div>
                    <p className="truncate text-xs text-slate-500">{d.slug} · {d.activity_count ?? 0} actividades</p>
                  </div>
                  <button type="button" onClick={() => toggleMutation.mutate(d)}
                    title={live ? 'Ocultar' : 'Mostrar'}
                    className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                    {live ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                  <button type="button" onClick={() => startEdit(d)} aria-label="Editar"
                    className="rounded-full border border-slate-200 p-2 text-slate-600 transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label="Eliminar"
                    onClick={() => {
                      const n = d.activity_count ?? 0;
                      if (n) {
                        setMsg({
                          ok: '',
                          err: `"${destName(d)}" tiene ${n} actividades. Cámbialas de destino primero, o usa el ojo para ocultarlo.`,
                        });
                        return;
                      }
                      if (window.confirm(`¿Eliminar "${destName(d)}" definitivamente?`)) deleteMutation.mutate(d.slug);
                    }}
                    className="rounded-full border border-slate-200 p-2 text-red-500 transition hover:border-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
            {!items.length && !listQuery.isLoading ? (
              <p className="py-8 text-center text-sm text-slate-500">Sin destinos.</p>
            ) : null}
          </div>
          <Pagination page={page} pageCount={pageCount} onPageChange={setPage} pageSize={pageSize} onPageSizeChange={setPageSize} pageSizes={[20, 50, 100]} />
        </section>

        <section className="min-w-0 h-fit rounded-[28px] border border-slate-200 bg-white p-6 shadow-[0_28px_80px_-36px_rgba(11,28,38,0.24)]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-[#14213d]">{editingSlug ? 'Editar destino' : 'Nuevo destino'}</h2>
            {editingSlug ? (
              <button type="button" onClick={resetForm} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#1fa5a3]">
                <X className="h-4 w-4" />Cancelar
              </button>
            ) : null}
          </div>

          <form onSubmit={submit} className="mt-5 space-y-3">
            <div className="rounded-2xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center gap-1.5">
                {LANGS.map((l) => {
                  const filled = Boolean((form.name?.[l.id] || '').trim());
                  return (
                    <button key={l.id} type="button" onClick={() => setLang(l.id)} title={l.name}
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition ${
                        lang === l.id ? 'bg-[#14213d] text-white' : 'border border-slate-200 text-slate-600 hover:border-[#1fa5a3] hover:text-[#1fa5a3]'
                      }`}>
                      {l.label}
                      <span className={`h-1.5 w-1.5 rounded-full ${filled ? 'bg-[#1fa5a3]' : lang === l.id ? 'bg-white/40' : 'bg-slate-300'}`} />
                    </button>
                  );
                })}
                <span className="ml-auto text-xs text-slate-500">{LANGS.find((l) => l.id === lang)?.name}</span>
              </div>
              <input
                className={`${inputCls} mt-3`}
                placeholder={`Nombre (${lang.toUpperCase()})${lang === 'es' ? ' · obligatorio' : ''}`}
                value={form.name?.[lang] || ''}
                onChange={(e) => {
                  setI18n(e.target.value);
                  if (!editingSlug && lang === 'es') set('slug', slugify(e.target.value));
                }}
              />
            </div>

            <div className="rounded-2xl border border-slate-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Imagen</p>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-16 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-slate-100">
                  {form.image ? <img src={form.image} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <button type="button" onClick={() => setPicker(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-2 text-xs font-semibold text-[#14213d] transition hover:border-[#1fa5a3] hover:text-[#1fa5a3]">
                  <ImagePlus className="h-3.5 w-3.5" />Elegir o subir
                </button>
              </div>
              <input className={`${inputCls} mt-3`} type="url" placeholder="…o pega una URL"
                value={form.image} onChange={(e) => set('image', e.target.value)} />
            </div>

            <input className={inputCls} placeholder="URL (ej. gran-canaria)" value={form.slug}
              onChange={(e) => set('slug', e.target.value)} disabled={Boolean(editingSlug)} required />

            <label className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
              <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
              <span className="text-sm font-semibold text-[#14213d]">Visible en la web</span>
            </label>

            <button type="submit" disabled={saveMutation.isPending}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1fa5a3] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#188b89] disabled:opacity-60">
              {editingSlug ? <MapPin className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {saveMutation.isPending ? 'Guardando…' : editingSlug ? 'Guardar cambios' : 'Crear destino'}
            </button>
          </form>
        </section>
      </div>

      <ImagePicker open={picker} title="Imagen del destino" onClose={() => setPicker(false)}
        onSelect={(url) => { set('image', url); setPicker(false); }} />
    </AdminSection>
  );
}
